import { useRouter } from "components/hooks";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { uploadImageToS3, validationForShareRequestMedia } from "utils/HelperFunctions";
import moment from "moment";
import { patch, post } from "utils/request";
import Button from "components/atoms/buttons/Button";
import { toast } from "react-toastify";

import { publishMemento } from "apis/magnets";
import { getCreateShareRequestEndpoint, getUpdateShareRequestRoute } from "apis/shareRequest";

import { LoaderContext } from "components/context/LoaderContext";
import { useContext, useState } from "react";
import { pickBy } from "lodash";
import FormLayoutWithModal from "components/organisms/activations/FormLayoutWithModal";
import { useQueryClient } from "@tanstack/react-query";
import ShareRequestModal from "components/organisms/ShareRequest/ShareRequestModal";
import { GLOBAL_LOADER_TEXT, TOAST_CONFIG } from "../../constants";
import ShareRequestForm from "./ShareRequestForm";
import { remove } from "../../utils/request";

const shareRequestSchema = yup.object().shape({
  name: yup.string().trim().required("* Required field"),
  description: yup.string().trim().required("* Required field").nullable().max(500),
  start_time: yup.date().required("* Required field").typeError("Invalid start date").nullable(),
  // TODO: Add image url validation.
  end_time: yup
    .date()
    .required("* Required field")
    .typeError("Invalid expiry date")
    .min(yup.ref("start_time"), "Expiry date should greater than start date")
    .nullable(),
  thumbnail: yup.mixed().when("thumbnail_url", {
    is: (thumbnail_url: string) => !thumbnail_url,
    then: validationForShareRequestMedia,
  }),
  post_data: yup.object().shape({
    is_media_shared: yup.boolean().required(),
    post_content: yup
      .string()
      .max(180, "Post content should be not more than 180 characters")
      .required("* Required field"),
  }),
  media_type: yup.string().required(),
});

//  "name": "abcderf12312",
//  "description": "description ehre",
//  "image_url": "https://stackoverflow.com/questions/51514567",
//  "start_time": 1674503138078,
//  "end_time": 1683500794101,
//  "claim_instructions": "Really long text

function ShareRequest({
  magnetDetails,
  shareRequestExistingValues,
  isClaimCodePublished,
}: {
  magnetDetails: BrandMagnetDetails;
  shareRequestExistingValues?: ShareRequestCreateFormData;
  isClaimCodePublished?: boolean;
}) {
  const router = useRouter();
  const { shareRequestId } = router.query;
  const [isBrandPreviewModalOpen, setIsBrandPreviewModalOpen] = useState(false);

  const queryClient = useQueryClient();

  const { loading, setLoading } = useContext(LoaderContext);

  const formMethods = useForm<ShareRequestCreateFormData | ShareRequestEditFormData>({
    resolver: yupResolver(shareRequestSchema),
    defaultValues: shareRequestExistingValues || {
      name: "",
      description: "",
      thumbnail_url: undefined,
      start_time: moment(),
      end_time: moment().add(3, "days"),
      post_data: {
        is_media_shared: false,
        post_content: "",
      },
      media_type: "",
      media_url: "",
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isDirty },
    watch,
  } = formMethods;

  const handlePublishShareRequestDrop = (mementoId: string) => {
    post(
      publishMemento(magnetDetails.id, mementoId),
      {},
      {
        processData: () => {
          setLoading(false);
          toast.success("Share Request Published Successfully", TOAST_CONFIG);
          router.go(-1);
        },
        processError: () => {
          setLoading(false);
          toast.error("Failed to Publish.", TOAST_CONFIG);
        },
      },
    );
  };

  const handleSaveAsDraftOrPublish = async (formData: ShareRequestCreateFormData, shouldPublish: boolean = false) => {
    setLoading(true, GLOBAL_LOADER_TEXT.UPLOADING_MEDIA);
    const imageUrl = formData.thumbnail ? await uploadImageToS3(formData.thumbnail) : undefined;

    if (!imageUrl) {
      toast.error("Something went wrong while we tried to upload your media. Try Again", TOAST_CONFIG);
      return;
    }
    setLoading(true, GLOBAL_LOADER_TEXT.UPDATING_MEMENTO);
    const formDataPayload = {
      ...formData,
      thumbnail_url: formData.media_type.startsWith("video") ? magnetDetails.image_url : imageUrl,
      media_url: imageUrl,
      media_type: formData.media_type,
      start_time: formData.start_time?.valueOf(),
      end_time: formData.end_time?.valueOf(),
      post_data: formData.post_data,
    };

    post(getCreateShareRequestEndpoint(magnetDetails.id), formDataPayload, {
      processData: ({
        data,
      }: {
        data: {
          id: string;
        };
      }) => {
        if (shouldPublish) {
          handlePublishShareRequestDrop(data.id);
          return;
        }
        toast.success("Saved Claim Code As Draft", TOAST_CONFIG);
        setLoading(false);
        router.go(-1);
      },
      processError: (error) => {
        console.log(error);
      },
    });
  };

  const handleDeleteShareRequestSuccess = () => {
    setLoading(false);
    toast.success("Share Request Deleted successfully", TOAST_CONFIG);
    router.go(-1);
  };

  const handleDeleteShareRequestError = (errorMessage: string) => {
    setLoading(false);
    toast.error(errorMessage, TOAST_CONFIG);
  };

  const handleDeleteShareRequest = () => {
    setLoading(true);
    remove(getUpdateShareRequestRoute(magnetDetails.id, (shareRequestExistingValues as any)?.id), {
      processData: handleDeleteShareRequestSuccess,
      processError: handleDeleteShareRequestError,
    });
  };

  const handleUpdateOrPublish = async (data: ShareRequestCreateFormData, shouldPublish: boolean = false) => {
    try {
      setLoading(true, GLOBAL_LOADER_TEXT.UPLOADING_MEDIA);
      const thumbnailUrl = data.thumbnail ? await uploadImageToS3(data.thumbnail) : undefined;

      setLoading(true, GLOBAL_LOADER_TEXT.UPDATING_MEMENTO);
      const cleanedObject = pickBy(
        {
          ...data,
          thumbnail_url: thumbnailUrl,
          thumbnail: undefined,
          start_time: data.start_time?.valueOf(),
          end_time: data.end_time?.valueOf(),
        },
        (v) => v !== undefined,
      );

      const formData = cleanedObject;

      await patch(getUpdateShareRequestRoute(magnetDetails.id, (shareRequestExistingValues as any)?.id), formData, {
        processData: () => {
          queryClient.invalidateQueries({
            queryKey: ["share-request-id", magnetDetails.id, (shareRequestExistingValues as any).id],
          });
          if (shouldPublish) {
            handlePublishShareRequestDrop(shareRequestId);
            return;
          }

          toast.success("Updated Claim", TOAST_CONFIG);
          setLoading(false);
          router.go(-1);
        },
        processError: (error) => console.log(error),
      });
    } catch (reason) {
      setLoading(false);
    }
  };

  const onPreviewModalClose = () => {
    setIsBrandPreviewModalOpen(false);
  };

  const handleSaveAsDraftButtonClick = (formData: ShareRequestCreateFormData) =>
    shareRequestExistingValues ? handleUpdateOrPublish(formData) : handleSaveAsDraftOrPublish(formData);

  const handlePublishShareRequest = (formData: ShareRequestCreateFormData) => {
    if (shareRequestExistingValues) {
      handleUpdateOrPublish(formData, true);
    } else {
      handleSaveAsDraftOrPublish(formData, true);
    }
  };

  const handlePreviewButtonClick = () => {
    setIsBrandPreviewModalOpen(true);
  };

  return (
    <FormProvider
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...formMethods}
    >
      {isBrandPreviewModalOpen && (
        <ShareRequestModal
          id=""
          brandId={magnetDetails.brand_id}
          endTime={watch("end_time")}
          startTime={watch("start_time")}
          onClose={onPreviewModalClose}
          name={watch("name")}
          brandName={magnetDetails.brand_name}
          magnetId={magnetDetails.id}
          magnetName={magnetDetails.name}
          magnetImageUrl={magnetDetails.image_url}
          description={watch("description")}
          postData={watch("post_data")}
          autoAirdropOnPurchase={watch("auto_drop_on_purchase")}
          thumbnailUrl={watch("thumbnail") ? URL.createObjectURL(watch("thumbnail") as File) : watch("thumbnail_url")}
          mediaUrl={watch("thumbnail") ? URL.createObjectURL(watch("thumbnail") as File) : watch("media_url")}
          mediaType={watch("media_type")}
          publishHandler={handleSubmit(handlePublishShareRequest)}
          isBrandPreview
          disabled={loading}
          dropStatus={0}
        />
      )}

      <FormLayoutWithModal
        magnetImageUrl={magnetDetails.image_url}
        magnetName={magnetDetails.name}
        brandName={magnetDetails.brand_name}
        activationType="shareRequest"
        isDirty={isDirty}
      >
        <div className=" flex justify-between">
          {/* Forms */}
          <p className="font-satoshi text-xs font-medium italic leading-6 text-secondary 2xl:text-sm">
            Disclaimer: Once the Share Request is published, this data can&apos;t be edited.
          </p>
        </div>
        <div className="mt-4">
          <ShareRequestForm />
        </div>
      </FormLayoutWithModal>
      {/* {!isMerchDropPublished && ( */}
      <div className="mt-3 flex w-full justify-end space-x-2 px-24 py-2 shadow-2xl 2xl:px-24 2xl:py-3">
        {shareRequestExistingValues && (
          <Button
            variant="whiteOutline"
            text="DELETE"
            additionalClasses="xl:px-6 xl:py-3 xl:text-xs 2xl:text-sm 2xl:px-8 2xl:py-4"
            onClick={handleDeleteShareRequest}
          />
        )}
        <Button
          variant="rustPinkSolid"
          text={shareRequestExistingValues ? "UPDATE" : "SAVE AS DRAFT"}
          additionalClasses="xl:px-6 xl:py-3 xl:text-xs 2xl:text-sm 2xl:px-8 2xl:py-4"
          onClick={handleSubmit(handleSaveAsDraftButtonClick)}
        />
        {!isClaimCodePublished && (
          <Button
            text="PREVIEW"
            additionalClasses="xl:px-6 xl:py-3 xl:text-xs 2xl:text-sm 2xl:px-8 2xl:py-4"
            onClick={handleSubmit(handlePreviewButtonClick)}
            variant="magnetiqSolid"
          />
        )}
      </div>
    </FormProvider>
  );
}

ShareRequest.propTypes = {};

export default ShareRequest;
